import {
  CurrencyCode,
  currencyMap,
  toMajorUnits,
  toMinorUnits,
} from './currency'

export type MoneyOptions = Omit<
  Intl.NumberFormatOptions,
  'currency' | 'currencySign'
> & {
  locale?: string
  forceLocale?: boolean
  hideDecimalsIfZero?: boolean
  hideDecimals?: boolean
  roundingMode?: string
}

export type ShortFormatAmountProps = Omit<
  MoneyOptions,
  'notation' | 'compactDisplay'
> & {
  /* If true, attempts to remove the currency code from the formatted string. */
  hideCurrencyIfLong?: boolean
}

export type Money = {
  /** The amount in minor units (e.g., cents). */
  amount: number
  /** The ISO 4217 currency code. */
  currency: CurrencyCode
}

type FormatResult = {
  parts: Intl.NumberFormatPart[]
  isCompact: boolean
  majorAmount: number
  optionsUsed: Intl.NumberFormatOptions
}

// --- CORE FUNCTIONS ---

const getEffectiveLocale = (
  locale = 'en-US',
  currency: CurrencyCode,
  forceLocale = false
): string => {
  const isUSDinGBLocale = currency === 'USD' && locale === 'en-GB'
  const showUsdNarrow = !forceLocale && isUSDinGBLocale
  return showUsdNarrow ? 'en-US' : locale
}

const formatMoneyInternal = (
  amount: Money,
  formatOptions: Intl.NumberFormatOptions,
  locale?: string,
  forceLocale?: boolean
): FormatResult => {
  const majorAmount = toMajorUnits(amount.amount, amount.currency)
  const effectiveLocale = getEffectiveLocale(
    locale,
    amount.currency,
    forceLocale
  )

  let currentOptions = formatOptions

  try {
    new Intl.NumberFormat(effectiveLocale, currentOptions).format(majorAmount)
  } catch (err) {
    if (
      err instanceof RangeError &&
      err.message.includes('currencyDisplay') &&
      currentOptions.currencyDisplay !== 'symbol'
    ) {
      currentOptions = {
        ...formatOptions,
        currencyDisplay: 'symbol',
      }
    } else {
      throw err
    }
  }

  const formatter = new Intl.NumberFormat(effectiveLocale, currentOptions)
  const parts = formatter.formatToParts(majorAmount)
  const isCompact = !!parts.find((p) => p.type === 'compact')?.value

  return {
    parts,
    isCompact,
    majorAmount,
    optionsUsed: currentOptions,
  }
}

const getStandardFormatOptions = (
  amount: Money,
  options: MoneyOptions,
  majorAmount: number
): Intl.NumberFormatOptions => {
  const {
    hideDecimalsIfZero = false,
    hideDecimals = false,
    ...otherOptions
  } = options
  const { minorUnits } = currencyMap[amount.currency]
  const isInteger = Number.isInteger(majorAmount)

  const fractionDigits =
    hideDecimals || (hideDecimalsIfZero && isInteger) ? 0 : minorUnits

  return {
    style: 'currency',
    currency: amount.currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...otherOptions,
  }
}

const getCompactFormatOptions = (
  amount: Money,
  options: ShortFormatAmountProps,
  majorAmount: number
): Intl.NumberFormatOptions => {
  const {
    hideCurrencyIfLong = false,
    hideDecimalsIfZero = true,
    hideDecimals = true,
    ...props
  } = options

  const isOverThousand = Math.abs(majorAmount) > 1000
  const isInteger = Number.isInteger(majorAmount)
  const { minorUnits } = currencyMap[amount.currency]

  const fractionDigits =
    hideDecimals || (hideDecimalsIfZero && isInteger) ? 0 : minorUnits

  return {
    ...props,
    style: 'currency',
    currency: amount.currency,
    currencyDisplay: hideCurrencyIfLong ? 'code' : 'symbol',
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    maximumSignificantDigits:
      props.maximumSignificantDigits ?? (isOverThousand ? 3 : undefined),
  }
}

const joinFormattedParts = (
  parts: Intl.NumberFormatPart[],
  hideCurrency: boolean
): string => {
  const partsToUse = hideCurrency
    ? parts.filter((p) => p.type !== 'currency')
    : parts

  return partsToUse
    .map((p) => p.value)
    .join('')
    .trim()
}

// --- PUBLIC API ---

export const createMoney = (
  majorAmount: number,
  currency: CurrencyCode
): Money => ({
  amount: toMinorUnits(majorAmount, currency),
  currency,
})

export const formatMoney = (
  amount: Money,
  options: MoneyOptions = {}
): string => {
  const majorAmount = toMajorUnits(amount.amount, amount.currency)
  const formatOptions = getStandardFormatOptions(amount, options, majorAmount)

  const result = formatMoneyInternal(
    amount,
    formatOptions,
    options.locale,
    options.forceLocale
  )
  return joinFormattedParts(result.parts, false)
}

export const formatMoneyShort = (
  amount: Money,
  options: ShortFormatAmountProps = {}
) => {
  const { hideCurrencyIfLong = false, locale = 'en-US' } = options
  const majorAmount = toMajorUnits(amount.amount, amount.currency)

  const commonFormatProps: ShortFormatAmountProps = {
    ...options,
    locale,
    hideDecimalsIfZero: true,
    hideDecimals: true,
  }

  const lang = locale.split('-')[0]
  const isCJKLocale = lang === 'ja' || lang === 'ko' || lang === 'zh'
  const compactionBoundary = isCJKLocale ? 10000 : 1000
  const shouldCompact = Math.abs(majorAmount) >= compactionBoundary

  if (shouldCompact) {
    const compactOptions = getCompactFormatOptions(
      amount,
      commonFormatProps,
      majorAmount
    )
    const compactResult = formatMoneyInternal(
      amount,
      compactOptions,
      options.locale,
      options.forceLocale
    )

    if (compactResult.isCompact) {
      return joinFormattedParts(compactResult.parts, hideCurrencyIfLong)
    }
  }
  const truncatedMajorAmount = Math.trunc(majorAmount)
  const truncatedMoney = createMoney(truncatedMajorAmount, amount.currency)

  const standardOptions = getStandardFormatOptions(
    truncatedMoney,
    commonFormatProps,
    truncatedMajorAmount
  )
  const standardResult = formatMoneyInternal(
    truncatedMoney,
    standardOptions,
    options.locale,
    options.forceLocale
  )

  return joinFormattedParts(standardResult.parts, false)
}

const assertSameCurrency = (monies: Money[]): CurrencyCode => {
  const currency = monies[0].currency
  if (monies.some((m) => m.currency !== currency)) {
    throw new Error('all currencies must be the same when calculating money')
  }
  return currency
}

export const sumMoney = (...monies: [Money, ...Money[]]): Money => {
  const currency = assertSameCurrency(monies)
  const amount = monies.reduce((acc, m) => acc + m.amount, 0)

  return { amount, currency }
}

export const subtractMoney = (...monies: [Money, ...Money[]]): Money => {
  const currency = assertSameCurrency(monies)
  const [first, ...rest] = monies
  const amount = rest.reduce((acc, m) => acc - m.amount, first.amount)

  return { amount, currency }
}
